import { getIdentityNowSourcesCache } from '@src/endpoints/custom.endpoints'
import { getCorrelatedSources } from '@src/endpoints/openapis/saas-cam-correlated-sources'
import { useDeepCompareEffect, useOpenApiRequest } from '@src/hooks'
import { useAuth } from '@src/modules/oauth/context'
import { getIdnAPIURL } from '@src/selectors'
import { filter } from 'lodash'
import React, { PropsWithChildren } from 'react'
import { useSelector } from 'react-redux'
import {
  actions,
  InitialState,
  initialState,
  reducer,
  SourcesCache,
  SourcesCacheContext,
  useSourcesCacheContext
} from './context'
import { useApiRequestWithTimmer } from './hooks'

const intervalRefreshCall = 15 * 60000

interface LocalCache {
  state?: InitialState
}
const cache: LocalCache = {}

interface Props {}

const SourcesCacheContainer: React.FC<Props> = React.memo(
  ({ children }: PropsWithChildren<Props>) => {
    const [fetchCounter, setFetchCorrelatedSources] = React.useState(0)
    const urlRequest = useSelector(getIdnAPIURL)
    const [state, dispatch] = React.useReducer(
      reducer,
      cache?.state || initialState
    )
    cache.state = state
    const { options } = useAuth()
    const tenantId = options?.oauthOptions?.tenantId
    const res = useApiRequestWithTimmer(
      {
        request: getIdentityNowSourcesCache,
        reqParams: {
          params: {},
          urlRequest
        }
      },
      intervalRefreshCall
    )

    const correlatedSourcesResponse = useOpenApiRequest(
      {
        request: getCorrelatedSources,
        reqParams: {}
      },
      [fetchCounter]
    )

    function fetchCorrelatedSources() {
      setFetchCorrelatedSources(prev => prev + 1)
    }

    useDeepCompareEffect(() => {
      const { response = [], pending } = correlatedSourcesResponse
      if (tenantId) {
        const list = filter(response, x => x?.tenantId === tenantId) || []
        dispatch(actions.loadCorrelatedSources({ list, pending }))
      }
    }, [correlatedSourcesResponse, tenantId])

    useDeepCompareEffect(() => {
      const { response = [], pending } = res
      dispatch(
        actions.loadIdentityNowSources({ list: response as any, pending })
      )
    }, [res])

    const contextValue = React.useMemo<SourcesCache>(
      () => ({
        state,
        dispatch,
        fetchCorrelatedSources
      }),
      [state, dispatch]
    )

    return (
      <SourcesCacheContext.Provider value={contextValue}>
        {children}
      </SourcesCacheContext.Provider>
    )
  }
)

export { useSourcesCacheContext }

const wrapper: React.FC<{}> = React.memo(
  ({ children }: PropsWithChildren<{}>) => (
    <SourcesCacheContainer>{children}</SourcesCacheContainer>
  )
)

export default wrapper
